import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../lib/api';
import { User, Shield, Settings, LogOut, Camera, AlertCircle, Loader2, Bell, Check, Trash2, Moon, Smartphone, Mail, Lock, Shield as ShieldIcon, ArrowRight } from 'lucide-react';

export default function Profile() {
  const { user, logout, refreshUser, updateProfile } = useAuth();
  const [avatarPreview] = useState<string | null>(user?.avatarUrl || null);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      await updateProfile(formData);
      await refreshUser();
      setSuccess('Profil mis à jour avec succès');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const settingsSections = [
    {
      title: 'Compte',
      icon: User,
      items: [
        { label: 'Informations personnelles', desc: 'Nom, email, téléphone', to: '/profile' },
        { label: 'Sécurité', desc: 'Mot de passe, 2FA, sessions', to: '/profile/security' },
        { label: 'Notifications', desc: 'Push, email, SMS', to: '/profile/notifications' },
      ],
    },
    {
      title: 'Préférences',
      icon: Settings,
      items: [
        { label: 'Apparence', desc: 'Thème, langue, unités', to: '/profile/appearance' },
        { label: 'Confidentialité', desc: 'Données, partage, RGPD', to: '/profile/privacy' },
      ],
    },
  ];

  const dangerZoneItems = [
    {
      icon: Trash2,
      title: 'Supprimer le compte',
      desc: 'Supprimer définitivement votre compte et toutes vos données',
      action: 'Supprimer',
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-28">
        <div className="flex items-center justify-between mb-6 animate-fade-in">
          <h1 className="text-xl font-semibold text-text">Mon profil</h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2 animate-slide-up">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2 animate-slide-up">
            <Check size={16} />
            {success}
          </div>
        )}

        {/* Avatar Section */}
        <div className="card animate-fade-in mb-5">
          <div className="text-center mb-4">
            <div className="relative w-28 h-28 rounded-full bg-gray-200 mx-auto mb-4 overflow-hidden">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User size={56} className="mx-auto text-gray-400" />
              )}
              <span className="absolute bottom-0 right-0 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white shadow-lg" title="Modification de la photo bientôt disponible">
                <Camera size={18} />
              </span>
            </div>
            <p className="text-sm text-text-secondary">Modification de la photo bientôt disponible</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="input-group">
              <label htmlFor="fullName" className="input-label">Nom complet</label>
              <input
                id="fullName"
                type="text"
                className="input-field"
                value={formData.fullName}
                onChange={e => setFormData({...formData, fullName: e.target.value})}
                placeholder="Votre nom complet"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="email" className="input-label">Email</label>
              <input
                id="email"
                type="email"
                className="input-field"
                value={user?.email || ''}
                disabled
              />
              <p className="input-hint">L'email ne peut pas être modifié</p>
            </div>

            <div className="input-group">
              <label htmlFor="phone" className="input-label">Téléphone</label>
              <input
                id="phone"
                type="tel"
                className="input-field"
                value={user?.phone || ''}
                disabled
              />
              <p className="input-hint">Le téléphone ne peut pas être modifié</p>
            </div>

            <div className="p-4 bg-primary/5 rounded-xl border border-primary/10">
              <div className="flex items-center gap-3">
                <ShieldIcon size={20} className="text-primary" />
                <div>
                  <p className="font-medium text-text">Vérification de compte</p>
                  <p className="text-sm text-text-secondary">Votre compte est vérifié et sécurisé</p>
                </div>
                <span className="badge badge-success badge-sm ml-auto">Vérifié</span>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="loading-spinner" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder les modifications'
              )}
            </button>
          </form>
        </div>

        {/* Settings Sections */}
        <div className="space-y-5">
          {settingsSections.map((section, _sectionIndex) => (
            <div key={section.title} className="card animate-slide-up" style={{animationDelay: `${_sectionIndex * 100}ms`}}>
              <div className="flex items-center gap-2 mb-4">
                <section.icon size={20} className="text-primary" />
                <h3 className="font-semibold text-text">{section.title}</h3>
              </div>
              <div className="space-y-2">
                {section.items.map((item, _itemIndex) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="flex items-center justify-between p-3 rounded-xl transition-all duration-200 hover:bg-gray-50 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <section.icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-text group-hover:text-primary transition-colors">{item.label}</p>
                        <p className="text-sm text-text-secondary">{item.desc}</p>
                      </div>
                      <ArrowRight size={18} className="text-text-secondary group-hover:text-primary transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Danger Zone */}
        <div className="card border-red-100 animate-slide-up">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 size={20} className="text-error" />
            <h3 className="font-semibold text-text">Zone de danger</h3>
          </div>
          <div className="space-y-3">
            {dangerZoneItems.map((item, _index) => (
              <div key={item.title} className="p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <item.icon size={20} className="text-error" />
                    </div>
                    <div>
                      <p className="font-medium text-text">{item.title}</p>
                      <p className="text-sm text-text-secondary">{item.desc}</p>
                    </div>
                  </div>
                  <button className={`btn btn-${item.variant} btn-sm flex-shrink-0`}>
                    {item.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logout */}
        <div className="card animate-slide-up">
          <div className="flex items-center gap-2 mb-4">
            <LogOut size={20} className="text-error" />
            <h3 className="font-semibold text-text">Déconnexion</h3>
          </div>
          <p className="text-sm text-text-secondary mb-4">Vous serez déconnecté de tous vos appareils.</p>
          <button onClick={logout} className="btn btn-danger">
            <LogOut size={18} /> Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}